import React, { useRef, useState, useMemo, useEffect } from 'react';
import swal from 'sweetalert';

export default function CreateCalibration({
    buildId,
    initialSelections = {},
    suffixes = [],
    calibrations,
    products,
    newCalibration,
    setNewCalibration,
    saveNewCalibration
}) {

    const smartphoneRef = useRef();
    const earphoneRef = useRef();
    const boneHeadRef = useRef();
    const wireRef = useRef();
    const suffixRef = useRef();
    const serialNumberRef = useRef();

    const [originFilter, setOriginFilter] = useState('');
    const [calibrationOriginId, setCalibrationOriginId] = useState('');

    const filteredCalibrations = useMemo(() => {
        const q = originFilter.trim().toLowerCase();
        if (!q) return calibrations;
        return calibrations.filter((c) =>
            String(c.kitname || '').toLowerCase().includes(q) ||
            String(c.serial_number || '').toLowerCase().includes(q) ||
            String(c.id).includes(q)
        );
    }, [calibrations, originFilter]);

    useEffect(() => {
        if (!calibrationOriginId) return;
        if (!filteredCalibrations.some((c) => String(c.id) === String(calibrationOriginId))) {
            setCalibrationOriginId('');
            setNewCalibration((prev) => ({ ...prev, calibration_origin: NaN }));
        }
    }, [filteredCalibrations, calibrationOriginId, setNewCalibration]);

    const selectDefault = (v) => (!v || v === 'all') ? '' : v;
    const suffixDefault = () => {
        const s = initialSelections.suffix;
        if (!s || s === 'all' || s === 'no suffix') return 'no suffix';
        return s;
    };

    const preventWheel = field => {
        document.getElementById(field + 'Input').blur();
    }

    const handleChange = (e) => {
        e.preventDefault();
        setNewCalibration((prev) => ({
            ...prev,
            [e.target.name]: parseInt(e.target.value, 10),
            calibration_build: parseInt(buildId, 10),
        }));
    };

    const onCalibrationOriginChange = (e) => {
        e.stopPropagation();
        const v = e.target.value;
        setCalibrationOriginId(v);
        setNewCalibration((prev) => ({
            ...prev,
            calibration_origin: v ? parseInt(v, 10) : NaN,
            calibration_build: parseInt(buildId, 10),
        }));
    };

    const setProductAndKitname = () => {
        var productList = [];
        var first, second, third, fourth;
        var i = 0;
        const phoneName = smartphoneRef.current?.value;
        const earName = earphoneRef.current?.value;
        const boneName = boneHeadRef.current?.value;
        const wireName = wireRef.current?.value;
        while (i < products.length) {
            if (products[i].name === phoneName) {
                productList[0] = products[i].id;
                first = products[i].name + '_';
            }
            if (products[i].name === earName) {
                productList[1] = products[i].id;
                second = products[i].name + '_';
            }
            if (products[i].name === boneName) {
                productList[2] = products[i].id;
                third = products[i].name + '_';
            }
            if (products[i].name === wireName) {
                productList[3] = products[i].id;
                fourth = products[i].name;
            }
            i++
        }
        var kitname = first + second + third + fourth;
        const sufVal = suffixRef.current?.value;
        if (sufVal && sufVal !== 'all' && sufVal !== 'no suffix') {
            kitname += '_' + sufVal;
        }
        const serial = (serialNumberRef.current?.value ?? '').trim();
        if (serial) {
            kitname += '-' + serial;
        }
        return [productList, kitname];
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        const serialTrimmed = (serialNumberRef.current?.value ?? '').trim();
        if (serialTrimmed.length > 20) {
            swal(
                'Datos no válidos',
                'El número de serie admite como máximo 20 caracteres.',
                'warning'
            );
            return;
        }
        const [productList, kitname] = setProductAndKitname();
        newCalibration.product = productList;
        newCalibration.kitname = kitname;
        newCalibration.serial_number = serialTrimmed || null;
        newCalibration.calibration_origin = calibrationOriginId
            ? parseInt(calibrationOriginId, 10)
            : NaN;
        if (newCalibration.kitname.includes('undefined') ||
            newCalibration.kitname.includes('NaN') ||
            isNaN(newCalibration.mic_constant) ||
            isNaN(newCalibration.noise_constant) ||
            isNaN(newCalibration.noise_floor) ||
            isNaN(newCalibration.dbhl_floor) ||
            isNaN(newCalibration.dbhl_roof) ||
            newCalibration.mic_constant.toString().length > 5 ||
            newCalibration.noise_constant.toString().length > 5 ||
            newCalibration.noise_floor.toString().length > 5 ||
            newCalibration.dbhl_floor.toString().length > 5 ||
            newCalibration.dbhl_roof.toString().length > 5 ||
            isNaN(newCalibration.calibration_origin)) {
            swal(
                'Datos no válidos',
                `   - Debe seleccionar todos los tipos de accesorios.
                    - Los parámetros de ruido deben tener entre 1 y 5 caracteres.
                    - Debe seleccionar una calibración para copiar sus valores.
                `,
                'warning'
            )
            return
        }
        if (newCalibration.kitname.length > 200) {
            swal(
                'Datos no válidos',
                'El kitname resultante supera 200 caracteres. Acorte el número de serie o revise los accesorios.',
                'warning'
            );
            return;
        }
        saveNewCalibration(newCalibration)
    }

    return (
        <>
            <form id="newCalibrationForm" onSubmit={(e) => handleSubmit(e)}>
                <div className=" d-flex justify-content-between border border-secondary rounded p-2 p-xl-3 my-3 flex-wrap">
                    <div className="form-group col-12 col-lg-2">
                        <label htmlFor="smartphone">Smartphone</label>
                        <select name="smartphone" className="form-select" ref={smartphoneRef} defaultValue={selectDefault(initialSelections.smartphone)}>
                            <option value="">Seleccione</option>
                            {products.map((item, idx) => {
                                if (item.type === 'phone') {
                                    return <option key={idx} value={item.name}>{item.name}</option>
                                }
                                return null;
                            })}
                        </select>
                    </div>

                    <div className="form-group col-12 mt-2 col-lg-2 mt-lg-0">
                        <label htmlFor="earphone">Auricular</label>
                        <select name="earphone" className="form-select" ref={earphoneRef} defaultValue={selectDefault(initialSelections.earphone)}>
                            <option value="">Seleccione</option>
                            {products.map((item, idx) => {
                                if (item.type === 'earphone') {
                                    return <option key={idx} value={item.name}>{item.name}</option>
                                }
                                return null;
                            })}
                        </select>
                    </div>

                    <div className="form-group col-12 my-2 col-lg-2 my-lg-0">
                        <label htmlFor="bone head">Vincha</label>
                        <select name="bone head" className="form-select" ref={boneHeadRef} defaultValue={selectDefault(initialSelections.boneHead)}>
                            <option value="">Seleccione</option>
                            {products.map((item, idx) => {
                                if (item.type === 'bone head') {
                                    return <option key={idx} value={item.name}>{item.name}</option>
                                }
                                return null;
                            })}
                        </select>
                    </div>

                    <div className="form-group col-12 mb-2 col-lg-2 mb-lg-0">
                        <label htmlFor="wire">Cable</label>
                        <select name="wire" className="form-select" ref={wireRef} defaultValue={selectDefault(initialSelections.wire)}>
                            <option value="">Seleccione</option>
                            {products.map((item, idx) => {
                                if (item.type === 'wire') {
                                    return <option key={idx} value={item.name}>{item.name}</option>
                                }
                                return null;
                            })}
                        </select>
                    </div>

                    <div className="form-group col-12 col-lg-2">
                        <label htmlFor="suffix">Sufijo</label>
                        <select name="suffix" className="form-select" ref={suffixRef} defaultValue={suffixDefault()}>
                            <option value="no suffix">Sin sufijo</option>
                            {suffixes.map((suffix, idx) => (
                                <option key={idx} value={suffix.text}>{suffix.text}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group col-12 px-2 px-xl-3">
                    <label htmlFor="serial_number_new_cal">Número de serie</label>
                    <input
                        id="serial_number_new_cal"
                        type="text"
                        ref={serialNumberRef}
                        className="form-control"
                        placeholder="Ej. NBK_00002724 (opcional)"
                        defaultValue={initialSelections.serialNumber ?? ''}
                        autoComplete="off"
                    />
                    <small className="text-muted d-block mt-1">
                        Si lo completa, se concatena al kitname con &quot;-&quot; (mismo criterio que en búsqueda por número de serie).
                    </small>
                </div>

                <div className="d-flex justify-content-between flex-wrap" onChange={(e) => handleChange(e)}>
                    <div className="form-group col-12 col-lg-2">
                        <label htmlFor="mic_constant">Mic constant</label>
                        <input id="mic_constantInput" type="number" step="0.0001" name="mic_constant" className="form-control"
                            onWheel={() => preventWheel('mic_constant')} />
                    </div>

                    <div className="form-group col-12 mt-2 col-lg-2 mt-lg-0">
                        <label htmlFor="noise_constant">Noise const.</label>
                        <input id="noise_constantInput" type="number" step="0.0001" name="noise_constant" className="form-control"
                            onWheel={() => preventWheel('noise_constant')} />
                    </div>

                    <div className="form-group col-12 my-2 col-lg-2 my-lg-0">
                        <label htmlFor="noise_floor">Noise floor</label>
                        <input id="noise_floorInput" type="number" step="0.0001" name="noise_floor" className="form-control"
                            onWheel={() => preventWheel('noise_floor')} />
                    </div>

                    <div className="form-group col-12 mb-2 col-lg-2 mb-lg-0">
                        <label htmlFor="dbhl_floor">dBHL floor</label>
                        <input id="dbhl_floorInput" type="number" step="0.0001" name="dbhl_floor" className="form-control"
                            onWheel={() => preventWheel('dbhl_floor')} />
                    </div>

                    <div className="form-group col-12 col-lg-2">
                        <label htmlFor="dbhl_roof">dBHL roof</label>
                        <input id="dbhl_roofInput" type="number" step="0.0001" name="dbhl_roof" className="form-control"
                            onWheel={() => preventWheel('dbhl_roof')} />
                    </div>
                </div>

                <div className="col-12 col-lg-8 mt-3" onChange={(e) => handleChange(e)}>
                    <label htmlFor="calibration_origin_filter">Copiar valores de calibración</label>
                    <input
                        id="calibration_origin_filter"
                        type="search"
                        className="form-control mb-2"
                        placeholder="Filtrar por kitname, número de serie o id…"
                        value={originFilter}
                        onChange={(e) => {
                            e.stopPropagation();
                            setOriginFilter(e.target.value);
                        }}
                        autoComplete="off"
                    />
                    <select
                        id="calibration_origin"
                        name="calibration_origin"
                        className="form-select"
                        value={calibrationOriginId}
                        onChange={onCalibrationOriginChange}
                    >
                        <option value="">Seleccione</option>
                        {filteredCalibrations.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.kitname}
                                {item.serial_number ? ` · ${item.serial_number}` : ''}
                            </option>
                        ))}
                    </select>
                    {originFilter.trim() !== '' && (
                        <small className="text-muted d-block mt-1">
                            {filteredCalibrations.length} de {calibrations.length} coincidencias
                        </small>
                    )}
                </div>

                <input
                    id='submit_new_calibration'
                    type="submit"
                    value="GUARDAR"
                    className="btn btn-info text-white d-block mx-auto col-lg-3 mt-4"
                />

            </form>
        </>
    )

}